import React, { useEffect, useState } from "react";
import { apiRequest } from "../utils/api";
import "../styles/ScheduleDayModal.css";

function ScheduleDayModal({ storeId, selectedDate, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    category: "",
    due_date: selectedDate,
    start_datetime: "",
    end_datetime: "",
    is_all_day: true,
    is_done: false,
    memo: "",
  });

  const fetchData = async () => {
    try {
      const [itemRes, categoryRes] = await Promise.all([
        apiRequest(`inventory/schedules/?date=${selectedDate}&store=${storeId}`, "GET"),
        apiRequest(`inventory/schedule-categories/`, "GET"),
      ]);

      setItems(Array.isArray(itemRes) ? itemRes : (itemRes?.results || []));
      setCategories(Array.isArray(categoryRes) ? categoryRes : []);
    } catch (error) {
      console.error("modal fetch error:", error);
      setItems([]);
      setCategories([]);
    }
  };

  useEffect(() => {
    if (!selectedDate) return;
    setForm((prev) => ({
      ...prev,
      due_date: selectedDate,
    }));
    fetchData();
  }, [selectedDate, storeId]);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      title: "",
      category: "",
      due_date: selectedDate,
      start_datetime: "",
      end_datetime: "",
      is_all_day: true,
      is_done: false,
      memo: "",
    });
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      alert("タイトルを入力してください");
      return;
    }

    const payload = {
      title: form.title.trim(),
      category: form.category || null,
      due_date: form.due_date || null,
      start_datetime: form.start_datetime || null,
      end_datetime: form.end_datetime || null,
      is_all_day: form.is_all_day,
      is_done: form.is_done,
      memo: form.memo || "",
      store: storeId || null,
    };

    try {
      if (editingId) {
        await apiRequest(`inventory/schedules/${editingId}/`, "PATCH", payload);
      } else {
        await apiRequest(`inventory/schedules/`, "POST", payload);
      }

      resetForm();
      fetchData();
      onSaved?.();
    } catch (error) {
      console.error("save error:", error);
      alert("保存に失敗しました");
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      category: item.category || "",
      due_date: item.due_date || selectedDate,
      start_datetime: item.start_datetime ? item.start_datetime.slice(0, 16) : "",
      end_datetime: item.end_datetime ? item.end_datetime.slice(0, 16) : "",
      is_all_day: !!item.is_all_day,
      is_done: !!item.is_done,
      memo: item.memo || "",
    });
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`「${item.title}」を削除しますか？`)) return;

    try {
      await apiRequest(`inventory/schedules/${item.id}/`, "DELETE");
      if (editingId === item.id) resetForm();
      fetchData();
      onSaved?.();
    } catch (error) {
      console.error("delete error:", error);
      alert("削除に失敗しました");
    }
  };

  return (
    <div className="schedule-modal-overlay" onClick={onClose}>
      <div className="schedule-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="schedule-modal-header">
          <h2>{selectedDate} の予定</h2>
          <button type="button" onClick={onClose}>閉じる</button>
        </div>

        <form className="schedule-modal-form" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="タイトル"
            value={form.title}
            onChange={(e) => handleChange("title", e.target.value)}
          />

          <div className="schedule-modal-row">
            <select
              value={form.category}
              onChange={(e) => handleChange("category", e.target.value)}
            >
              <option value="">カテゴリーなし</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>

            <input
              type="date"
              value={form.due_date}
              onChange={(e) => handleChange("due_date", e.target.value)}
            />
          </div>

          <div className="schedule-modal-row">
            <input
              type="datetime-local"
              value={form.start_datetime}
              onChange={(e) => handleChange("start_datetime", e.target.value)}
            />
            <input
              type="datetime-local"
              value={form.end_datetime}
              onChange={(e) => handleChange("end_datetime", e.target.value)}
            />
          </div>

          <div className="schedule-modal-checks">
            <label>
              <input
                type="checkbox"
                checked={form.is_all_day}
                onChange={(e) => handleChange("is_all_day", e.target.checked)}
              />
              終日
            </label>
            <label>
              <input
                type="checkbox"
                checked={form.is_done}
                onChange={(e) => handleChange("is_done", e.target.checked)}
              />
              処理済み
            </label>
          </div>

          <textarea
            rows="3"
            placeholder="メモ"
            value={form.memo}
            onChange={(e) => handleChange("memo", e.target.value)}
          />

          <div className="schedule-modal-top-actions">
            <button type="submit">{editingId ? "更新" : "登録"}</button>
            {editingId && (
              <button type="button" onClick={resetForm}>新規に戻す</button>
            )}
          </div>
        </form>

        <div className="schedule-modal-list">
          {items.length === 0 ? (
            <div className="schedule-empty">予定はありません</div>
          ) : (
            items.map((item) => (
              <div key={item.id} className={`schedule-modal-item ${item.is_done ? "is-done" : ""}`}>
                <div className="schedule-modal-item-main">
                  <div className="schedule-title-row">
                    <strong>{item.title}</strong>
                    {item.category_name && (
                      <span className="schedule-badge">{item.category_name}</span>
                    )}
                  </div>
                  <div className="schedule-meta">
                    {item.due_date && <span>期限: {item.due_date}</span>}
                    {item.start_datetime && <span>開始: {item.start_datetime.slice(11, 16)}</span>}
                  </div>
                  {item.memo ? <div className="schedule-modal-memo">{item.memo}</div> : null}
                </div>

                <div className="schedule-modal-item-actions">
                  <button type="button" onClick={() => handleEdit(item)}>編集</button>
                  <button type="button" className="danger" onClick={() => handleDelete(item)}>削除</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default ScheduleDayModal;